import { pathOrderedModuleNumbers, plannedWeekOf } from '@mio/shared';

import type { ActivityKind, FullActivity, FullModule, ProgramFull } from '@mio/shared';

export type ActivityStatus = 'not_started' | 'in_progress' | 'complete';

export interface ActivityView extends FullActivity {
  status: ActivityStatus;
  module: { number: number; title: string };
  previous: { id: string; code: string; title: string; kind: ActivityKind } | null;
  next: { id: string; code: string; title: string; kind: ActivityKind } | null;
  capabilityDetails: { slug: string; title: string; isEdge: boolean }[];
}

export interface ModuleView extends FullModule {
  status: 'current' | 'complete' | 'upcoming';
  completedCount: number;
  activityCount: number;
  /** Activities grouped for display, in the order the learner takes them. */
  groups: {
    kind: 'lesson' | 'workshop' | 'checkpoint' | 'field';
    label: string;
    activities: (FullActivity & { status: ActivityStatus })[];
  }[];
}

export const statusOf = (program: ProgramFull, id: string): ActivityStatus =>
  program.progress[id] ?? 'not_started';

export function allActivities(
  program: ProgramFull,
): (FullActivity & { moduleNumber: number; moduleTitle: string })[] {
  return program.modules.flatMap((w) =>
    w.activities.map((a) => ({ ...a, moduleNumber: w.number, moduleTitle: w.title })),
  );
}

/**
 * Activities in the order "next" searches them: the modules on the learner's saved path
 * first, in curriculum order, then the rest. Without a path this is the curriculum order.
 */
export function pathOrderedActivities(program: ProgramFull) {
  const all = allActivities(program);
  if (!program.path) return all;
  const order = pathOrderedModuleNumbers(
    program.path,
    program.modules.map((module) => module.number),
  );
  const rank = new Map(order.map((number, index) => [number, index]));
  return [...all].sort((a, b) => (rank.get(a.moduleNumber) ?? 0) - (rank.get(b.moduleNumber) ?? 0));
}

/** The module of the first unfinished activity on the learner's path; the last module when all is done. */
export function currentModuleOf(program: ProgramFull): number {
  const first = pathOrderedActivities(program).find((a) => statusOf(program, a.id) !== 'complete');
  return first?.moduleNumber ?? program.modules.at(-1)?.number ?? 1;
}

export function completionOf(program: ProgramFull): number {
  const all = allActivities(program);
  if (all.length === 0) return 0;
  const done = all.filter((a) => statusOf(program, a.id) === 'complete').length;
  return Math.round((done / all.length) * 1000) / 10;
}

export function nextActivity(program: ProgramFull) {
  return pathOrderedActivities(program).find((a) => statusOf(program, a.id) !== 'complete') ?? null;
}

const GROUPS: {
  kind: ModuleView['groups'][number]['kind'];
  label: string;
  kinds: ActivityKind[];
}[] = [
  { kind: 'lesson', label: 'Lessons', kinds: ['lesson'] },
  { kind: 'workshop', label: 'Workshops', kinds: ['workshop'] },
  { kind: 'checkpoint', label: 'Checkpoint', kinds: ['checkpoint'] },
  { kind: 'field', label: 'Workbench', kinds: ['field_beat', 'field_day'] },
];

export function moduleView(program: ProgramFull, number: number): ModuleView | null {
  const module = program.modules.find((w) => w.number === number);
  if (!module) return null;
  const current = currentModuleOf(program);
  const withStatus = module.activities.map((a) => ({ ...a, status: statusOf(program, a.id) }));
  const completedCount = withStatus.filter((a) => a.status === 'complete').length;
  const status: ModuleView['status'] =
    withStatus.length > 0 && completedCount === withStatus.length
      ? 'complete'
      : number === current
        ? 'current'
        : 'upcoming';
  return {
    ...module,
    status,
    completedCount,
    activityCount: withStatus.length,
    groups: GROUPS.map((g) => ({
      kind: g.kind,
      label: g.label,
      activities: withStatus.filter((a) => g.kinds.includes(a.kind)),
    })).filter((g) => g.activities.length > 0),
  };
}

export function moduleSummaries(program: ProgramFull) {
  return program.modules
    .map((w) => moduleView(program, w.number))
    .filter((w): w is ModuleView => w !== null);
}

const OPENABLE: ActivityKind[] = ['lesson', 'checkpoint', 'workshop'];

export function activityView(program: ProgramFull, id: string): ActivityView | null {
  const all = allActivities(program);
  const index = all.findIndex((a) => a.id === id);
  const row = all[index];
  if (!row) return null;
  // Neighbours skip Workbench activities, which have nowhere to open until that flow is built.
  const previous = all
    .slice(0, index)
    .reverse()
    .find((a) => OPENABLE.includes(a.kind));
  const next = all.slice(index + 1).find((a) => OPENABLE.includes(a.kind));
  return {
    ...row,
    status: statusOf(program, row.id),
    module: { number: row.moduleNumber, title: row.moduleTitle },
    previous: previous
      ? { id: previous.id, code: previous.code, title: previous.title, kind: previous.kind }
      : null,
    next: next ? { id: next.id, code: next.code, title: next.title, kind: next.kind } : null,
    capabilityDetails: program.capabilities
      .filter((c) => row.capabilities.includes(c.slug))
      .map((c) => ({ slug: c.slug, title: c.title, isEdge: c.isEdge })),
  };
}

/** Provisional capability levels from completion, mirroring the API's derivation. */
export function capabilityLevels(program: ProgramFull) {
  const evidence = new Map<string, { kinds: Set<ActivityKind>; week: number }>();
  for (const a of allActivities(program)) {
    if (statusOf(program, a.id) !== 'complete') continue;
    for (const slug of a.capabilities) {
      const e = evidence.get(slug) ?? { kinds: new Set<ActivityKind>(), week: 0 };
      e.kinds.add(a.kind);
      e.week = Math.max(e.week, plannedWeekOf(program.modules, a.moduleNumber, a.day));
      evidence.set(slug, e);
    }
  }
  return program.capabilities.map((c) => {
    const e = evidence.get(c.slug);
    let level = 'not_introduced';
    if (e) {
      if (e.week >= 8 && (e.kinds.has('field_beat') || e.kinds.has('field_day')))
        level = 'demonstrated_independently';
      else if (e.kinds.has('checkpoint')) level = 'demonstrated_with_assistance';
      else if (e.kinds.has('workshop')) level = 'practised_with_guidance';
      else level = 'introduced';
    }
    return { ...c, level };
  });
}
